import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, BookOpen, Calendar, LogOut, User, ArrowRight, Lock } from 'lucide-react';

const ICON_URL = 'https://customer-assets.emergentagent.com/job_4bf56672-c145-4c09-96ef-4b0ea795a4e7/artifacts/opuwvjaf_Brujula_ICON-3.png';

const Portal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const quickLinks = [
    {
      id: 'courses',
      title: 'My Courses',
      description: 'Access your enrolled courses and track progress',
      icon: BookOpen,
      link: '/courses',
      available: true
    },
    {
      id: 'events',
      title: 'Upcoming Events',
      description: 'View and register for workshops and webinars',
      icon: Calendar,
      link: '/events',
      available: true
    },
    {
      id: 'chat',
      title: 'Chat with Diana',
      description: 'Get personalized guidance and support',
      icon: MessageCircle,
      link: '#',
      available: false
    },
    {
      id: 'resources',
      title: 'Resource Library',
      description: 'Download workbooks, templates, and guides',
      icon: BookOpen,
      link: '#',
      available: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="portal-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container-custom py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <p className="text-brand-turquoise font-accent text-sm mb-1">Client Portal</p>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue">
                Welcome back, {user?.name?.split(' ')[0] || 'there'}!
              </h1>
              <p className="text-gray-600 mt-2">
                Your space to learn, grow, and connect.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-brand-turquoise/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-brand-turquoise" />
                </div>
                <div>
                  <p className="font-medium text-brand-blue text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Section */}
          <div className="lg:col-span-2 space-y-8">
            {/* Chat Coming Soon Banner */}
            <div className="bg-gradient-to-r from-brand-turquoise/10 to-brand-lime/10 rounded-2xl p-8 border border-brand-turquoise/20" data-testid="chat-coming-soon">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-brand-turquoise/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-7 h-7 text-brand-turquoise" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-brand-blue mb-2">
                    Chat Feature Coming Soon
                  </h2>
                  <p className="text-gray-600 mb-4">
                    We're building a personalized chat experience where you can connect 
                    directly with Diana for career guidance, mentorship, and support. 
                    Stay tuned!
                  </p>
                  <span className="badge-lime inline-block">In Development</span>
                </div>
              </div>
            </div>

            {/* Quick Links Grid */}
            <div>
              <h2 className="font-heading font-bold text-xl text-brand-blue mb-6">
                Quick Access
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.id}
                      className={`bg-white rounded-xl p-6 border border-gray-100 ${
                        item.available ? 'card-hover cursor-pointer' : 'opacity-60'
                      }`}
                      data-testid={`quick-link-${item.id}`}
                    >
                      {item.available ? (
                        <Link to={item.link} className="block">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-brand-turquoise/10 flex items-center justify-center">
                              <Icon className="w-6 h-6 text-brand-turquoise" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-heading font-bold text-brand-blue mb-1">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-600">{item.description}</p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </Link>
                      ) : (
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Lock className="w-6 h-6 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-heading font-bold text-gray-500 mb-1">
                              {item.title}
                            </h3>
                            <p className="text-sm text-gray-400">{item.description}</p>
                            <span className="text-xs text-brand-turquoise font-medium mt-2 inline-block">
                              Coming Soon
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Welcome Card */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <img src={ICON_URL} alt="STEMJECTORY" className="w-10 h-10" />
                <span className="font-heading font-bold text-brand-blue">STEMJECTORY</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                This is your personal portal to access courses, resources, and 
                connect with our community. More features are being added soon!
              </p>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-400">
                  Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently'}
                </p>
              </div>
            </div>

            {/* Need Help */}
            <div className="bg-brand-blue rounded-2xl p-6 text-white">
              <h3 className="font-heading font-bold text-lg mb-3">Need Help?</h3>
              <p className="text-gray-300 text-sm mb-4">
                Have questions or need support? We're here for you.
              </p>
              <Link
                to="/consulting"
                className="btn-secondary bg-white text-brand-blue hover:bg-gray-100 w-full text-center block"
              >
                Contact Us
              </Link>
            </div>

            {/* Account Actions */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="font-heading font-bold text-brand-blue mb-4">Account</h3>
              <div className="space-y-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-gray-50 text-gray-600 hover:text-brand-magenta transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portal;
